import React, { useCallback, useEffect } from "react";
import {
  EventMachine,
  EventMachineApiRequirements,
  UserForm,
} from "./EventMachine";
import { useActor, useSelector } from "@xstate/react";
import { IonContent, IonPage, IonSpinner, useIonAlert } from "@ionic/react";
import { ImageAsset } from "@greeter/core";
import { classNames, isType } from "@greeter/util";
import { Log } from "@greeter/log";
import {
  IconCalendar,
  IconClock,
  IconDownload,
  IconLocation,
  IconMail,
  IconTicket,
} from "@tabler/icons-react";
import { marked } from "marked";
import {
  BottomBar,
  Card,
  QuantityControls,
  ChatSupportBubble,
} from "@greeter/matter-v2";

import { formatMoney } from "@greeter/formatters";
import { AnimatePresence, HTMLMotionProps, motion } from "motion/react";
import { dropInOutFromBottom, dropInOutFromTop } from "@greeter/motion";
import {
  DateTimePeriod,
  FloatingBackButton,
  PulsingGreeter,
  Spinner,
  useAuth,
} from "@greeter/matter";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import { StripeLoader } from "@greeter-guest/components/Payments";
import { DatePeriod, TimeOfDay, withTimeOfDay } from "@greeter/date";
import { useFetchActorLoading } from "@greeter/xstate-util";
import { useSearchParams } from "@greeter/url";
import { addDays } from "date-fns";
import { ToastContainer, toast } from "react-toastify";

import { Helmet } from "@dr.pogodin/react-helmet";

import css from "./EventPage.module.scss";

function toDatePeriod(dateTimePeriod: DateTimePeriod): DatePeriod {
  const todFrom = TimeOfDay.fromObject(dateTimePeriod.timePeriod.from);
  const todTo = TimeOfDay.fromObject(dateTimePeriod.timePeriod.to);

  const isPastMidnight = todTo.getTotalSeconds() < todFrom.getTotalSeconds();

  Log.debug("[toDatePeriod]", { dateTimePeriod, isPastMidnight });

  return new DatePeriod({
    from: withTimeOfDay(new Date(dateTimePeriod.date), todFrom),
    to: withTimeOfDay(
      addDays(dateTimePeriod.date, isPastMidnight ? 1 : 0),
      dateTimePeriod.timePeriod.to
    ),
  });
}

function Title({ className, ...props }: React.ComponentProps<"h1">) {
  return <h1 {...classNames(className, css.Title)} {...props} />;
}

function Description({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      {...classNames(className, css.Description)}
      dangerouslySetInnerHTML={{
        __html:
          typeof children === "string" &&
          marked.parse(children, { breaks: true }),
      }}
      {...props}
    />
  );
}

function Content({ className, ...props }: React.ComponentProps<"div">) {
  return <div {...classNames(className, css.Content)} {...props} />;
}

function Display({ className, ...props }: React.ComponentProps<"div">) {
  return <div {...classNames(css.Display, className)} {...props} />;
}

Display.Text = function ({ className, ...props }: React.ComponentProps<"p">) {
  return <p {...classNames(className, css.DisplayText)} {...props} />;
};
Display.Icon = function (props: { icon: "location" | "calendar" | "clock" }) {
  return props.icon === "location" ? (
    <IconLocation className={css.DisplayIcon} />
  ) : props.icon === "clock" ? (
    <IconClock className={css.DisplayIcon} />
  ) : (
    <IconCalendar className={css.DisplayIcon} />
  );
};

const dateTimeFormatter = new Intl.DateTimeFormat("da-DK", {
  weekday: "short",
  month: "short",
  day: "numeric",
  hourCycle: "h23",
  hour: "2-digit",
  minute: "2-digit",
});

const dateFormatter = new Intl.DateTimeFormat("da-DK", {
  // dateStyle: "long",
  weekday: "long",
  month: "long",
  day: "numeric",
});
function DateDisplay(props: { date: Date }) {
  return (
    <Display>
      <Display.Icon icon="calendar" />
      <Display.Text style={{ textTransform: "capitalize" }}>
        {dateFormatter.format(props.date)}
      </Display.Text>
    </Display>
  );
}

const timeFormatter = new Intl.DateTimeFormat("da-DK", {
  timeStyle: "short",
  hourCycle: "h23",
});
function TimeSpanDisplay(props: { from: TimeOfDay; to: TimeOfDay }) {
  return (
    <Display>
      <Display.Icon icon="clock" />
      <Display.Text>
        {props.from.toString()} - {props.to.toString()}
      </Display.Text>
    </Display>
  );
}

function LocationDisplay(props: { address: string }) {
  return (
    <Display>
      <Display.Icon icon="location" />
      <Display.Text>{props.address}</Display.Text>
    </Display>
  );
}

/**
 * TODO: move styling to css
 */
function BuyTicketsButton(props: {
  quantity: number;
  unitPrice: number;
  onClick?: () => void;
}) {
  return (
    <Card
      variant="green"
      act="button"
      style={{
        width: "fit-content",
        borderRadius: 10,
      }}
      onClick={props.onClick}
    >
      <Card.Content
        preset="flex-aligned-center"
        padding="sm"
        style={{
          minWidth: 147,
          justifyContent: "space-between",
        }}
      >
        <IconTicket style={{ transform: "scale(0.8)" }} />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "end",
          }}
        >
          <span style={{ fontSize: 12, textAlign: "right" }}>Køb billet</span>
          <span
            style={{
              fontWeight: 300,
              textAlign: "right",
              fontSize: 12,
            }}
          >
            {formatMoney(props.unitPrice * props.quantity)} kr
          </span>
        </div>
      </Card.Content>
    </Card>
  );
}

function BuyButton(props: {
  mode: "invalid" | "initial" | "loading" | "complete";
  totalPrice: number;
  onClick?: () => void;
}) {
  return (
    <Card
      aria-disabled={props.mode === "invalid"}
      variant="green"
      style={{ borderRadius: 10, opacity: props.mode === "invalid" ? 0.5 : 1 }}
      act="button"
      onClick={props.onClick}
    >
      <Card.Content padding="sm">
        <AnimatePresence mode="popLayout">
          {props.mode === "invalid" && (
            <BuyButton.Content>
              <BuyButton.Title key="invalid-title">
                Udfyld felterne for at gennemføre købet
              </BuyButton.Title>
              <BuyButton.SubTitle key="total">
                Billetter: {formatMoney(props.totalPrice)} kr.
              </BuyButton.SubTitle>
            </BuyButton.Content>
          )}
          {props.mode === "initial" && (
            <BuyButton.Content>
              <BuyButton.Title key="initial-title">
                Tryk for at bekræfte køb
              </BuyButton.Title>
              <BuyButton.SubTitle key="total">
                Billetter: {formatMoney(props.totalPrice)} kr.
              </BuyButton.SubTitle>
            </BuyButton.Content>
          )}
          {props.mode === "loading" && (
            <BuyButton.Content>
              <PulsingGreeter key="pulse" />
              <BuyButton.Title key="loading">Køber...</BuyButton.Title>
            </BuyButton.Content>
          )}

          {props.mode === "complete" && (
            <BuyButton.Content>
              <BuyButton.Title key="complete-title">
                Du har købt billetterne
              </BuyButton.Title>
              <BuyButton.SubTitle key="complete-title">
                Billetterne er blevet sendt til din email
              </BuyButton.SubTitle>
            </BuyButton.Content>
          )}
        </AnimatePresence>
      </Card.Content>
    </Card>
  );
}

BuyButton.Content = function (props: HTMLMotionProps<"div">) {
  return <motion.div layout {...props} />;
};
BuyButton.Title = function (props: HTMLMotionProps<"p">) {
  return (
    <motion.p
      style={{ margin: 0, fontWeight: "bold", fontSize: 14 }}
      {...dropInOutFromTop}
      {...props}
    />
  );
};
BuyButton.SubTitle = function (props: HTMLMotionProps<"span">) {
  return (
    <motion.span style={{ fontSize: 12 }} {...dropInOutFromBottom} {...props} />
  );
};
BuyButton.Loading = function () {};

type PaymentFormProps = {
  onElements?: (element: StripeElements | null) => void;
  onStripe?: (stripe: Stripe | null) => void;
};
function PaymentForm(props: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();

  useEffect(() => props.onStripe?.(stripe), [stripe, props.onStripe]);
  useEffect(() => props.onElements?.(elements), [elements, props.onElements]);

  return (
    <PaymentElement
      id="payment"
      options={{
        wallets: { applePay: "auto", googlePay: "auto" },
        business: { name: "Greeter" },
        paymentMethodOrder: ["mobilepay", "apple_pay", "google_pay", "card"],
        layout: "tabs",
      }}
    />
  );
}

const stripePromise = StripeLoader.lazyStripePromise.value;

/**
 * TODO: We need to implement this https://docs.stripe.com/payments/accept-a-payment-deferred?platform=web&type=payment&client=react&lang=dotnet
 */
function PaymentMethod(props: { totalAsMinorUnit: number } & PaymentFormProps) {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        mode: "payment",
        amount: props.totalAsMinorUnit,
        captureMethod: "manual",
        currency: "dkk",
        paymentMethodTypes: ["mobilepay", "card"],
        appearance: { theme: "night" },
      }}
    >
      <PaymentForm {...props} />
    </Elements>
  );
}

function Terms() {
  return (
    <div
      style={{
        textAlign: "left",
        fontSize: "0.9rem",
        padding: "1rem 0",
      }}
    >
      Ved at trykke <q>Tryk for at bekræfte køb</q> anerkender du at du er 18+
      år gammel og at du accepterer{" "}
      <a
        href="https://www.greeter.dk/privatlivspoltik"
        target="_blank"
        style={{
          color: "var(--gm-color-alt-bg)",
          textDecoration: "underline",
        }}
      >
        privatlivspolitikken
      </a>{" "}
      og{" "}
      <a
        href="https://www.greeter.dk/salgs-og-leveringsbetingelser/"
        target="_blank"
        style={{
          color: "var(--gm-color-alt-bg)",
          textDecoration: "underline",
        }}
      >
        {" "}
        salgs- og leveringsbetingelserne
      </a>
    </div>
  );
}

export type EventPageProps = {
  slug: string;
  api: EventMachineApiRequirements;
};

export function EventPage(props: EventPageProps) {
  const searchParams = useSearchParams();
  const intent = searchParams.get("intent");
  const shouldCheckPayment = intent === "check-payment";
  const [snap, send, m] = useActor(EventMachine, {
    input: {
      api: props.api,
      stripe: stripePromise,
      slug: props.slug,
    },
  });

  const { auth } = useAuth();

  useEffect(() => {
    if (!auth) return;
    const unsub = auth.onAuthReady(() => {
      if (shouldCheckPayment) {
        send({ type: "checkPayment" });
      }
    });
    return unsub;
  }, [auth, shouldCheckPayment]);

  const quantity = useSelector(m, (s) => s.context?.quantity);
  const event = useSelector(m, (s) => s.context?.event);
  const tickets = useSelector(m, (s) => s.context?.event?.tickets);

  const isPlacingOrder = useFetchActorLoading(
    snap.context.api.placeTicketOrderRef
  );
  const isCheckingPayment = useFetchActorLoading(
    snap.context.api.checkPaymentRef
  );
  const isFetchingReceipt = useFetchActorLoading(
    snap.context.api.fetchTicketOrderReceiptRef
  );
  const isResendingReceipt = useFetchActorLoading(
    snap.context.api.resendTicketOrderReceiptRef
  );
  const isUpdatingCustomer = useFetchActorLoading(
    snap.context.api.updateCustomerRef
  );

  const userFormErrors = useSelector(m, (s) => s.context.userFormErrors);
  const userForm = useSelector(m, (s) => s.context.userForm);

  function hasError(field: keyof UserForm) {
    return (
      initialInteractions.has(field) &&
      ((userFormErrors.properties ?? {})[field]?.errors ?? []).length > 0
    );
  }

  const initialInteractions = useSelector(
    m,
    (s) => s.context.initialInteractions
  );

  const total =
    quantity * (isType(tickets, "paid-single") ? tickets?.price ?? 0 : 0);
  const totalAsMinorUnit = Number((total * 100).toFixed(0));

  function isInputDisabled() {
    return snap.context.api.placeTicketOrderRef
      .getSnapshot()
      .matches("fetching");
  }

  const onStripe = useCallback(
    (stripe: Stripe | null) => {
      send({
        type: "set.stripe",
        data: stripe ?? undefined,
      });
    },
    [send]
  );

  const onElements = useCallback(
    (elements: StripeElements | null) => {
      send({
        type: "set.stripeElements",
        data: elements ?? undefined,
      });
    },
    [send]
  );

  useEffect(() => {
    Log.debug("[EventPage]", { snap });
  }, [snap]);

  const salesPeriodEnd =
    event?.salesPeriod?.to ??
    (isType(event?.eventPeriod, "once") && toDatePeriod(event.eventPeriod).to);

  const expired = new Date() > salesPeriodEnd;

  const [alert, dismissAlert] = useIonAlert();

  useEffect(
    () =>
      snap.context.api.resendTicketOrderReceiptRef.on("fetched", () => {
        toast.success("✉️email sendt afsted", {
          position: "bottom-right",
        });
      }).unsubscribe,
    [m]
  );

  const eventJsonLd = event
    ? {
        "@context": "https://schema.org",
        "@type": "MusicEvent",
        name: event.title,
        description: event.description,
        location: {
          "@type": "Place",
          name: event.address,
          address: event.address,
        },
        startDate: isType(event.eventPeriod, "once")
          ? `${
              event.eventPeriod.date
            }T${event.eventPeriod.timePeriod.from.toString()}`
          : undefined,
        endDate: isType(event.eventPeriod, "once")
          ? `${
              event.eventPeriod.date
            }T${event.eventPeriod.timePeriod.to.toString()}`
          : undefined,
        image: event.coverAssets[0]
          ? ImageAsset.findUriWithSizeOrDefault(
              event.coverAssets[0],
              "16x9-w1024"
            )
          : undefined,
        offers: {
          "@type": "Offer",
          price: isType(event.tickets, "paid-single") ? event.tickets.price : 0,
          priceCurrency: "DKK",
          availability: "https://schema.org/InStock",
          url: `https://app.greeter.dk/events/${event.slug}`,
        },
        url: `https://app.greeter.dk/events/${event.slug}`,
      }
    : null;

  return (
    <IonPage>
      {event && (
        <Helmet>
          <title>{event.title}</title>
          <meta name="og:title" content={event.title} />
          <meta name="og:description" content={event.description} />
          <meta
            name="og:url"
            content={`https://app.greeter.dk/events/${event.slug}`}
          />
          {event.coverAssets[0] && (
            <meta
              name="og:image"
              content={ImageAsset.findUriWithSizeOrDefault(
                event.coverAssets[0],
                "16x9-w1024"
              )}
            />
          )}
          <script type="application/ld+json">
            {JSON.stringify(eventJsonLd)}
          </script>
        </Helmet>
      )}
      <IonContent>
        <AnimatePresence mode="popLayout">
          {snap.matches({ ui: "detail" }) && (
            <motion.div
              key="detail"
              className={css.EventPage}
              {...dropInOutFromBottom}
            >
              {event && (
                <div>
                  <div
                    className={css.Cover}
                    style={{
                      backgroundImage: `url(${ImageAsset.findUriWithSizeOrDefault(
                        event?.coverAssets[0],
                        "16x9-w1024"
                      )})`,
                    }}
                  />
                  <Content
                    style={{
                      position: "relative",
                      top: "20vh",
                      paddingBottom: "20vh",
                    }}
                  >
                    <Title>{event.title}</Title>
                    {isType(event.eventPeriod, "once") && (
                      <>
                        <DateDisplay date={new Date(event.eventPeriod.date)} />
                        <TimeSpanDisplay
                          from={event.eventPeriod.timePeriod.from}
                          to={event.eventPeriod.timePeriod.to}
                        />
                      </>
                    )}
                    <LocationDisplay address={event.address} />
                    <Description>{event.description}</Description>
                  </Content>
                </div>
              )}
            </motion.div>
          )}

          {snap.matches({ ui: "detail" }) && (
            <BottomBar theme="default" className={css.BottomBar}>
              <div className={css.BottomBarContent}>
                {expired && (
                  <p className={css.UnavailableText}>Forsalg lukket</p>
                )}
                {!expired && (
                  <>
                    <QuantityControls>
                      <QuantityControls.MinusButton
                        onClick={() => send({ type: "decrementQuantity" })}
                      />
                      <QuantityControls.Value
                        value={quantity ?? 0}
                        onChange={(ev) =>
                          send({
                            type: "set.quantity",
                            data: Number(ev.currentTarget.value),
                          })
                        }
                      />
                      <QuantityControls.PlusButton
                        onClick={() => send({ type: "incrementQuantity" })}
                      />
                    </QuantityControls>

                    <BuyTicketsButton
                      unitPrice={
                        isType(tickets, "paid-single") ? tickets.price : 0
                      }
                      quantity={quantity}
                      onClick={() => send({ type: "goto.cart" })}
                    />
                  </>
                )}
              </div>
            </BottomBar>
          )}

          {snap.matches({ ui: "cart" }) && (
            <motion.div
              key="cart"
              {...classNames(css.EventPage, css.CartPage)}
              {...dropInOutFromBottom}
            >
              {(snap.matches({ ui: { cart: "input" } }) ||
                snap.matches({ ui: { cart: "checkingPayment" } }) ||
                snap.matches({ ui: { cart: "placingTicketOrder" } }) ||
                snap.matches({ ui: { cart: "done" } })) && (
                <motion.div className={css.InputContent}>
                  <div>
                    <div
                      style={{
                        backgroundImage: `url(${ImageAsset.findUriWithSizeOrDefault(
                          event?.coverAssets[0],
                          "16x9-w1024"
                        )})`,
                      }}
                      className={css.CartCoverBackground}
                    />
                    <div className={css.CartCoverContent}>
                      <div
                        className={css.CartCoverThumb}
                        style={{
                          backgroundImage: `url(${ImageAsset.findUriWithSizeOrDefault(
                            event?.coverAssets[0],
                            "1x1-w256"
                          )})`,
                        }}
                      />
                      <div
                        className={css.CartCoverQuantity}
                        style={{
                          fontSize:
                            quantity >= 100 ? 16 : quantity >= 10 ? 18 : 20,
                        }}
                      >
                        {quantity} stk.
                      </div>
                      <div className={css.CartTitle}>{event?.title}</div>
                    </div>
                  </div>
                  {(snap.matches({ ui: { cart: "input" } }) ||
                    snap.matches({ ui: { cart: "checkingPayment" } }) ||
                    snap.matches({ ui: { cart: "placingTicketOrder" } })) && (
                    <Form>
                      <input
                        {...classNames(
                          css.Input,
                          hasError("firstName") && "error-outline",
                          isInputDisabled() && css.Disabled
                        )}
                        style={{ borderRadius: 10 }}
                        type="text"
                        placeholder="Fornavn"
                        value={userForm.firstName}
                        aria-label="Fornavn"
                        aria-invalid={hasError("firstName")}
                        onInput={(e) =>
                          send({
                            type: "set.firstName",
                            data: e.currentTarget.value,
                          })
                        }
                      />
                      {hasError("firstName") && (
                        <ErrorHint>Du skal indtaste dit fornavn</ErrorHint>
                      )}

                      <input
                        {...classNames(
                          css.Input,
                          hasError("lastName") && "error-outline",
                          isInputDisabled() && css.Disabled
                        )}
                        type="text"
                        placeholder="Efternavn"
                        value={userForm.lastName}
                        aria-label="Efternavn"
                        style={{ borderRadius: 10 }}
                        aria-invalid={hasError("lastName")}
                        onInput={(e) =>
                          send({
                            type: "set.lastName",
                            data: e.currentTarget.value,
                          })
                        }
                      />
                      {hasError("lastName") && (
                        <ErrorHint>Du skal indtaste dit efternavn</ErrorHint>
                      )}

                      <input
                        {...classNames(
                          css.Input,
                          hasError("email") && "error-outline",
                          isInputDisabled() && css.Disabled
                        )}
                        type="text"
                        placeholder="Email"
                        value={userForm.email}
                        aria-label="Email"
                        aria-invalid={hasError("email")}
                        style={{ borderRadius: 10 }}
                        onInput={(e) =>
                          send({
                            type: "set.email",
                            data: e.currentTarget.value,
                          })
                        }
                      />
                      {hasError("email") && (
                        <ErrorHint>Din email er ugyldig</ErrorHint>
                      )}

                      {totalAsMinorUnit > 0 && (
                        <PaymentMethod
                          totalAsMinorUnit={totalAsMinorUnit}
                          onElements={onElements}
                          onStripe={onStripe}
                        />
                      )}
                      <Terms />
                    </Form>
                  )}
                </motion.div>
              )}
              {snap.matches({ ui: { cart: "done" } }) && (
                <motion.div
                  layout
                  style={{
                    width: "100%",
                    padding: "0 1rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                  }}
                >
                  <p>
                    Vi har sendt dig billetterne på email til{" "}
                    <b>{snap.context.userForm.email}</b>!
                  </p>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto",
                    }}
                  >
                    <input
                      {...classNames(
                        css.Input,
                        hasError("email") && "error-outline",
                        isInputDisabled() && css.Disabled
                      )}
                      style={{ borderRadius: "10px 0 0 10px", zIndex: 1 }}
                      type="text"
                      placeholder="Email"
                      value={userForm.email}
                      aria-label="Email"
                      aria-invalid={hasError("email")}
                      onInput={(e) =>
                        send({
                          type: "set.email",
                          data: e.currentTarget.value,
                        })
                      }
                    />
                    <Card
                      variant="dark-blue"
                      onClick={() => {
                        send({ type: "resendReceipt" });
                      }}
                      disabled={snap.matches({ validation: "invalid" })}
                      onDisabledClick={() => {
                        alert({
                          mode: "ios",
                          header: "Du mangler at udfylde et felt",
                          message:
                            "Det ser ikke ud til at din email er gyldig. Opdater den til en gyldig email og prøv igen",
                          buttons: [{ text: "Ok" }],
                        });
                      }}
                      style={{
                        width: "fit-content",
                        height: "100%",
                        borderRadius: "0 10px 10px 0",
                      }}
                      act="button"
                    >
                      <Card.Content
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          gap: 5,
                        }}
                      >
                        {isResendingReceipt || isUpdatingCustomer ? (
                          <IonSpinner name="lines-small" />
                        ) : (
                          <IconMail style={{ color: "white" }} />
                        )}
                        Gensend
                      </Card.Content>
                    </Card>
                  </div>
                  <Card
                    variant="dark-blue"
                    onClick={() => {
                      send({ type: "downloadReceipt" });
                    }}
                    act="button"
                  >
                    <Card.Content
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        gap: 5,
                        minHeight: 60,
                      }}
                    >
                      {isFetchingReceipt ? (
                        <IonSpinner name="lines-small" />
                      ) : (
                        <IconDownload style={{ color: "white" }} />
                      )}
                      Download kvittering
                    </Card.Content>
                  </Card>
                </motion.div>
              )}
              {!snap.matches({ ui: { cart: "done" } }) && (
                <motion.div
                  layout
                  style={{
                    padding: 20,
                  }}
                >
                  <BuyButton
                    mode={
                      snap.matches({ ui: { cart: "done" } })
                        ? "complete"
                        : "initial"
                    }
                    totalPrice={total}
                    onClick={() => {
                      if (snap.matches({ validation: "invalid" })) {
                        alert({
                          mode: "ios",
                          header: "Du mangler at udfylde nogle felter!",
                          message:
                            "For at kunne købe billetterne, mangler du at udfylde nogle felter.",
                          buttons: [{ text: "Ok" }],
                        });
                        send({ type: "forceFirstInteraction" });
                        send({ type: "validate.userForm" });
                        return;
                      }

                      send({ type: "placeTicketOrder" });
                    }}
                  />
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        <FloatingBackButton onClick={() => send({ type: "back" })} />

        <Spinner
          reason={
            snap.matches({ ui: { cart: "placingTicketOrder" } }) ||
            isPlacingOrder
              ? "Opretter ordrer"
              : (snap.matches({ ui: { cart: "checkingPayment" } }) ||
                  isCheckingPayment) &&
                "Tjekker betalingen"
          }
        />

        <ChatSupportBubble
          offsetBottom={snap.matches({ ui: "cart" }) ? 0 : 70}
        />
      </IonContent>
      <ToastContainer />
    </IonPage>
  );
}

// type FormContextValue<T extends ZodTypeAny> = {
//   Root: React.ReactNode;
//   Input: typeof Input<T>;
// };
// function createFormContext<T extends ZodTypeAny>() {
//   return React.createContext<FormContextValue<T> | undefined>(undefined);
// }
// const UserFormContext = createFormContext<typeof UserFormSchema>();

// function Input<T extends ZodTypeAny>({
//   className,
//   ...props
// }: HTMLMotionProps<"input"> & {
//   name: keyof z.infer<T>;
// }) {}

function Form({ className, ...props }: HTMLMotionProps<"div">) {
  return <motion.div {...classNames(className, css.InputForm)} {...props} />;
}

function ErrorHint({ className, ...props }: HTMLMotionProps<"small">) {
  return (
    <motion.small
      {...classNames(className, css.ErrorHint)}
      {...dropInOutFromBottom}
      {...props}
    />
  );
}
